import { Select } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { loadGoogleMaps } from "../map/loadGoogleMaps"
import { LA_FALLBACK } from "../redux/global-map.constants"
import type { AnalyticsHeatmap, EntityType } from "../redux/global-map.types"

interface RecordDistributionHeatmapProps {
  heatmap: AnalyticsHeatmap
}

type EntityFilter = EntityType | "all"

// Grid cell size in degrees latitude (~0.08° ≈ 9km) — coarse enough that a
// filtered population in the hundreds still visibly clusters into a
// handful of density blobs rather than one dot per record.
const GRID_STEP_DEG = 0.08
const HEAT_COLOR = "#dc2626"

interface DensityCell {
  latitude: number
  longitude: number
  count: number
}

// Bins raw points into a coarse lat/lng grid, cell count = local record
// density — spec §9: "Heat intensity represents record density, not
// monetary value." Google's own `visualization.HeatmapLayer` would have
// been the more polished way to show this, but Google removed it from the
// Maps JavaScript API entirely as of v3.65 (confirmed live: "The Heatmap
// Layer functionality ... is no longer available" thrown at runtime, not
// just an incomplete type stub) — this draws the same density signal with
// `google.maps.Circle`, the same primitive this feature's own "Look Around
// a Zip Code" radius indicator already uses, sized and colored by each
// cell's own relative count instead of a fixed radius.
function binPointsToDensityCells(
  points: { latitude: number; longitude: number }[]
): DensityCell[] {
  const cellCounts = new Map<
    string,
    { latSum: number; lngSum: number; count: number }
  >()
  for (const point of points) {
    const latKey = Math.round(point.latitude / GRID_STEP_DEG)
    const lngKey = Math.round(point.longitude / GRID_STEP_DEG)
    const key = `${latKey}:${lngKey}`
    const existing = cellCounts.get(key)
    if (existing) {
      existing.latSum += point.latitude
      existing.lngSum += point.longitude
      existing.count += 1
    } else {
      cellCounts.set(key, {
        latSum: point.latitude,
        lngSum: point.longitude,
        count: 1
      })
    }
  }
  return Array.from(cellCounts.values()).map((cell) => ({
    latitude: cell.latSum / cell.count,
    longitude: cell.lngSum / cell.count,
    count: cell.count
  }))
}

/**
 * Spec §9: intensity = record density, not money. Fits to the filtered
 * points' own bounds; falls back to the LA default ONLY when there are no
 * valid coordinates at all. Points already carry only `{lat, lng,
 * entityType}` (`analytics.py::_heatmap` — no name/address ever reaches the
 * client, let alone the heatmap provider). No geocoding happens here —
 * every point is a pre-geocoded `ProjectLocation` row the server already
 * queried.
 */
export function RecordDistributionHeatmap({
  heatmap
}: RecordDistributionHeatmapProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const circlesRef = useRef<google.maps.Circle[]>([])
  const [entityFilter, setEntityFilter] = useState<EntityFilter>("all")
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then((maps) => {
      if (cancelled || !containerRef.current || mapRef.current) return
      mapRef.current = new maps.Map(containerRef.current, {
        center: { lat: LA_FALLBACK.latitude, lng: LA_FALLBACK.longitude },
        zoom: LA_FALLBACK.zoom,
        mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID",
        disableDefaultUI: true,
        gestureHandling: "cooperative"
      })
      setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!ready || !mapRef.current) return
    const map = mapRef.current

    circlesRef.current.forEach((circle) => circle.setMap(null))
    circlesRef.current = []

    const points = heatmap.points.filter(
      (point) => entityFilter === "all" || point.entityType === entityFilter
    )

    if (points.length === 0) {
      map.setCenter({ lat: LA_FALLBACK.latitude, lng: LA_FALLBACK.longitude })
      map.setZoom(LA_FALLBACK.zoom)
      return
    }

    const cells = binPointsToDensityCells(points)
    const maxCount = Math.max(...cells.map((cell) => cell.count))

    const bounds = new google.maps.LatLngBounds()
    points.forEach((point) =>
      bounds.extend({ lat: point.latitude, lng: point.longitude })
    )

    // A fixed km-scale radius was invisible for a nationally-spread filtered
    // population (live-verified: real data spanning multiple US regions
    // rendered zero visible circles at the resulting national-scale zoom —
    // a few km is sub-pixel against a continent). Scaling the base radius to
    // the data's own bounding-box diagonal keeps every density blob visually
    // meaningful whether the filtered population sits in one zip code or
    // spans the whole country. A flat equirectangular approximation (1° lat
    // ≈ 111km, 1° lng ≈ 111km·cos(lat)) is plenty accurate for sizing a
    // decorative density blob — no need for the separate "geometry" library
    // (real great-circle distance) just for this.
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const midLatRad = ((ne.lat() + sw.lat()) / 2) * (Math.PI / 180)
    const latSpanKm = Math.abs(ne.lat() - sw.lat()) * 111
    const lngSpanKm =
      Math.abs(ne.lng() - sw.lng()) * 111 * Math.max(Math.cos(midLatRad), 0.01)
    const diagonalMeters = Math.hypot(latSpanKm, lngSpanKm) * 1000
    const baseRadius = Math.min(
      Math.max(diagonalMeters * 0.035, 4_000),
      120_000
    )

    circlesRef.current = cells.map((cell) => {
      const intensity = cell.count / maxCount
      return new google.maps.Circle({
        map,
        center: { lat: cell.latitude, lng: cell.longitude },
        radius: baseRadius * (0.6 + intensity * 0.8),
        clickable: false,
        strokeWeight: 0,
        fillColor: HEAT_COLOR,
        fillOpacity: 0.2 + intensity * 0.45
      })
    })

    map.fitBounds(bounds, 24)
  }, [ready, heatmap, entityFilter])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
          <span>{t("globalMap.analytics.heatmap.low")}</span>
          <span
            aria-hidden
            className="h-2 w-16 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${HEAT_COLOR}26, ${HEAT_COLOR})`
            }}
          />
          <span>{t("globalMap.analytics.heatmap.high")}</span>
        </div>
        <Select<EntityFilter>
          size="small"
          value={entityFilter}
          onChange={setEntityFilter}
          className="w-28"
          options={[
            { value: "all", label: t("globalMap.analytics.heatmap.allTypes") },
            { value: "project", label: t("globalMap.entityLabel.project") },
            { value: "lead", label: t("globalMap.entityLabel.lead") }
          ]}
        />
      </div>
      <div
        ref={containerRef}
        data-testid="analytics-heatmap-canvas"
        className="h-[220px] w-full overflow-hidden rounded-md bg-gray-100"
      />
      {heatmap.missingCoordinateCount > 0 && (
        <p className="m-0 text-[11px] text-slate-400">
          {t("globalMap.analytics.heatmap.missingCoordinates", {
            count: heatmap.missingCoordinateCount
          })}
        </p>
      )}
    </div>
  )
}
